import { z } from "zod";
/**
 * Magic-link auth schemas — SSOT for the request + verify flow.
 *
 * Canon: docs/design-canon/04-shared/ssot.md.
 *
 * Two endpoints share this set:
 *   POST /api/auth/magic-link/request — issue a token, email it
 *   GET  /api/auth/magic-link/verify  — exchange token for session
 *
 * Plaintext tokens never live in the DB; the URL carries the
 * plaintext, the DB carries sha256(token). See
 * packages/db/src/schema/portal.ts for the storage rationale.
 */
// ─── Request ──────────────────────────────────────────────
/**
 * Body for POST /api/auth/magic-link/request.
 *
 * `tenantSlug` is NOT in the body — the API resolves the tenant
 * from the request host (`<slug>.portal.latitude30.co`). Letting the
 * caller name the tenant would defeat the host-based isolation.
 */
export const MagicLinkRequestSchema = z.object({
    email: z.string().email().toLowerCase(),
    /** Cloudflare Turnstile token. Required when CF_TURNSTILE_SECRET is
     *  configured at the API; the route validates server-side. Optional
     *  in the schema so lab/local builds without a site key can omit it
     *  (mirrors BookingCreateSchema). */
    turnstileToken: z.string().max(2048).optional(),
});
/**
 * Response from request — never echoes whether the email is
 * registered. Returns 202 with a constant body either way (defense
 * against email enumeration). The email contains the actual token
 * if the user exists; otherwise nothing is sent.
 */
export const MagicLinkRequestResponseSchema = z.object({
    ok: z.literal(true),
});
// ─── Verify ───────────────────────────────────────────────
/**
 * Query param for GET /api/auth/magic-link/verify?token=...
 *
 * Token is the plaintext 32-byte hex (64 chars). The route hashes
 * it sha256 and compares constant-time against `token_hash` in
 * `magic_link_tokens`.
 */
export const MagicLinkVerifyQuerySchema = z.object({
    token: z.string().regex(/^[0-9a-f]{64}$/, "invalid token format"),
});
/**
 * Verify endpoint redirects on success (302 → /portal) or failure
 * (302 → /portal/auth/error?reason=...). The shape below is for the
 * JSON variant used by tests; the production response is a redirect.
 */
export const MagicLinkVerifyResponseSchema = z.object({
    ok: z.literal(true),
    redirectTo: z.string().url(),
});
//# sourceMappingURL=magic-link.js.map